import type { World } from '../sim/types';
import { DEPTH_BINS, REEF_ISLANDS, stratumFloorKm2, stratumIndex, type ReefIsland } from '../sim/reef';
import { reefOf } from '../sim/data/sites';
import { epochDayOf } from '../sim/calendar';
import { ECONOMY } from '../sim/economy';
import { MONTH_DAYS } from '../sim/operations';
import { allocateSites, knownAt, stratumCv } from '../sim/areas';
import { classesOf, monthStep, stockFishing } from '../sim/fish';
import { PROTECTED_HERBIVORES, islandFishing, type Management } from '../sim/management';
import { REEF_SPECIES } from '../sim/systems/fish';
import { coralHistory } from './reefHealth';

/**
 * What an action is worth in the reef's own terms (M315, `docs/OPENING_LOOP_ROADMAP.md`'s G5, the fourth playtest's: "A
 * patrol costs about $21,574 monthly and changes forbidden catch from 31% to 29%. I can read those numbers, but I don't
 * know whether that is a worthwhile purchase. Hiring produces a 'closer count,' but its practical benefit is similarly
 * hard to judge"): read from the world as it stands, never written to it, so no tick computes anything else (the
 * fingerprint unmoved). No new rate: each figure is the simulation's own fishing and fish, or its survey's own error.
 */

/**
 * An island's grazing fish caught a month, kg (DERIVED from the simulation's own state, an ESTIMATE of the month ahead):
 * each protected herbivore's (`PROTECTED_HERBIVORES`, the fish a Herbivore Protection Area protects, the grazing fish
 * every page names) fish on the island today, class by class, times its weight, its fishing a year under a management
 * (`islandFishing`, the rate the tick's own step takes, M165, M170, M171) and the share of that class taken, over the
 * accounts' month (`MONTH_DAYS`). The instantaneous rate, Baranov's catch with the natural deaths and the predators left
 * out of the competition: against the death audit's fished kg over the 30 days after a new game (seed 42, `setDeathAudit`),
 * Maui's 2,219 kg estimated against 2,264 drawn, Molokaʻi's 3,432 against 3,339, Lānaʻi's 1,403 against 1,444 and
 * Kahoʻolawe's 192 against 186 (MEASURED, M315: within 3%, the draws' own spread among it). `islandFishing` keeps a cache
 * of each rule's fishing (`fishingBy`), filled the same whoever asks first, so a read here moves no figure the tick takes.
 */
export function grazingCaughtKgMonth(world: World, island: ReefIsland, management: Management): number {
  const s = world.sanctuaries[0];
  if (!s?.fish) return 0;
  const i = REEF_ISLANDS.indexOf(island);
  const day = epochDayOf(world);
  const step = monthStep(day);
  let kgYear = 0;
  for (const sp of REEF_SPECIES) {
    if (!PROTECTED_HERBIVORES.includes(sp.life.id)) continue;
    const fishing = islandFishing(sp, management, day)?.[i] ?? stockFishing(sp.supply);
    const counts = s.fish[sp.life.id].islands[i] ?? [];
    const classes = classesOf(sp.life)[step] ?? [];
    counts.forEach((n, c) => {
      const k = classes[c];
      if (!k) return;
      const taken = fishing.taken ? (fishing.taken[step]?.[c] ?? 0) : k.taken;
      kgYear += n * k.weightKg * fishing.perYear * taken;
    });
  }
  return (kgYear * MONTH_DAYS) / 365;
}

/** The world's management with one island's patrol level or protection changed: a copy, the world's own untouched. */
function changed(m: Management, island: ReefIsland, change: { patrol?: number; protect?: boolean }): Management {
  const i = REEF_ISLANDS.indexOf(island);
  return {
    ...m,
    patrols: m.patrols.map((p, j) => (j === i && change.patrol !== undefined ? change.patrol : p)),
    herbivoreProtection: m.herbivoreProtection.map((p, j) => (j === i && change.protect !== undefined ? change.protect : p)),
  };
}

/** What an action changes in an island's grazing fish caught: the catch a month now and with it, kg (DERIVED, `grazingCaughtKgMonth`). */
export interface CatchCut { now: number; with: number; fewer: number }

/**
 * A patrol's worth on an island: its grazing fish caught a month at a patrol level against Hawaiʻi's officers alone (the
 * level the island has where it is patrolled, the level offered where not), under every other rule as it stands (a
 * protected area in force there makes its whole catch forbidden, so a patrol cuts more of it). None without management.
 */
export function patrolCut(world: World, island: ReefIsland, level: number): CatchCut | null {
  const m = world.sanctuaries[0]?.management;
  if (!m) return null;
  const now = grazingCaughtKgMonth(world, island, changed(m, island, { patrol: 0 }));
  const after = grazingCaughtKgMonth(world, island, changed(m, island, { patrol: level }));
  return { now, with: after, fewer: now - after };
}

/**
 * A Herbivore Protection Area's worth on an island: its grazing fish caught a month with the protection in force against
 * without, at its patrols as they stand, on today's fish (an ESTIMATE: the rule is months away and the fish change).
 */
export function protectionCut(world: World, island: ReefIsland): CatchCut | null {
  const m = world.sanctuaries[0]?.management;
  if (!m) return null;
  const now = grazingCaughtKgMonth(world, island, changed(m, island, { protect: false }));
  const after = grazingCaughtKgMonth(world, island, changed(m, island, { protect: true }));
  return { now, with: after, fewer: now - after };
}

/**
 * The smallest change in an island's live coral, percentage points, one coral survey of it can tell from its last count
 * (ESTIMATE, from the survey's own error model): the game's own test of a change, past twice the two counts' combined
 * standard error (`reefHealth`'s `apart`, its findings' and the island card's), the last count's error as measured
 * (`coralHistory`'s island-wide), the new count's expected: its sites (`days` × its dive teams × `reefSitesPerDay`, as
 * `completeJob` lays them) over the island's depths by their floor (`allocateSites`), each depth's error NOAA's there
 * scaled by the square root of its sites (`stratumCv`) on the cover last known there (the expected value; a count's own
 * error is its drawn value's, `areas.ts`'s `drawn`), the depths weighed by their floor as `coralHistory` weighs them.
 * None where the island has no count or no reef.
 */
export function coralChangeSeen(world: World, island: ReefIsland, days: number, divers: number): number | null {
  const s = world.sanctuaries[0];
  const reef = s ? reefOf(s) : null;
  const last = coralHistory(world, island).at(-1);
  const teams = Math.floor(divers / 2);
  if (!reef || !last || teams <= 0 || days <= 0) return null;
  const i = REEF_ISLANDS.indexOf(island);
  const floor = stratumFloorKm2(reef);
  const laid = allocateSites(reef, [island], days * teams * ECONOMY.reefSitesPerDay);
  let area = 0;
  let variance = 0;
  for (let b = 0; b < DEPTH_BINS.length; b++) {
    const k = stratumIndex(i, b);
    const a = floor[k] ?? 0;
    const cv = stratumCv(reef, k, laid[k] ?? 0);
    const cover = knownAt(world, island, 'cover', b)?.value;
    if (cv === null || cover === undefined) return null;
    area += a;
    variance += (a * cv * cover) ** 2;
  }
  if (area <= 0) return null;
  return 2 * Math.hypot(last.se, Math.sqrt(variance) / area);
}

/** A figure as a player reads it, to two significant figures ("43", "1,300"): the estimate's own precision, no more. */
export function roughly(x: number): string {
  if (!Number.isFinite(x) || x <= 0) return '0';
  const digits = Math.max(0, Math.floor(Math.log10(x)) - 1);
  const r = Math.round(x / 10 ** digits) * 10 ** digits;
  return r.toLocaleString('en-US');
}
